import { createBaseThemeOptions, createUnifiedTheme, palettes } from '@backstage/theme';
import { Typography } from './mui.typography';
import { tokens } from '@equinor/eds-tokens';
import { pageThemes } from './mui.page-themes';

// Based on theme.varia.css for BUI
const VariaTheme = {
  primary: '#206f77', // --bui-bg-solid
  secondary: '#206f77', // --bui-bg-solid
  // Semantic foreground/accent colors — used as .main in MUI palette (icons, borders, labels on light surfaces)
  error: '#a50827', // --bui-fg-danger
  warning: '#8a4100', // --bui-fg-warning
  success: '#20691f', // --bui-fg-success
  info: '#015e8d', // --bui-fg-info
  // Semantic background fill colors — used for alert/banner backgrounds
  infoBackground: '#b5daf5', // --bui-bg-info
  errorBackground: '#bc002a', // --bui-bg-danger
  warningBackground: '#9b4900', // --bui-bg-warning
  backgroundDefault: '#f5f5f5', // --bui-bg (canvas)
  backgroundPaper: '#ffffff', // --bui-bg-neutral-1
  chipBackground: '#ececec', // --bui-bg-neutral-2 (light)
  chipHoverBackground: '#205c62', // --varia-background-color-button-hover-lightmode
  chipPressedBackground: '#20565c', // --varia-background-color-button-pressed-lightmode
  chipTextColor: '#ffffff', // --bui-fg-solid (light) — white text on dark teal
  text: {
    primary: '#1d1d1d', // --varia-text-color-primary-lightmode
    secondary: '#585858', // --varia-text-color-secondary-lightmode
  },
  navigation: {
    background: '#132634', // varia specific color
    submenu: {
      background: '#243746', // varia specific color
    },
  },
} as const;

export const themeLight = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: VariaTheme.primary,
      },
      secondary: {
        main: VariaTheme.secondary,
      },
      status: {
        ...palettes.light.status,
        error: '#ffffff',
      },
      error: {
        main: VariaTheme.error,
      },
      warning: {
        main: VariaTheme.warning,
      },
      info: {
        main: VariaTheme.info,
      },
      success: {
        main: VariaTheme.success,
      },
      errorBackground: VariaTheme.errorBackground,
      warningBackground: VariaTheme.warningBackground,
      infoBackground: VariaTheme.infoBackground,
      background: {
        default: VariaTheme.backgroundDefault,
        paper: VariaTheme.backgroundPaper,
      },
      navigation: {
        background: VariaTheme.navigation.background,
        indicator: tokens.colors.interactive.primary__resting.hex,
        color: tokens.colors.text.static_icons__primary_white.hex,
        selectedColor: tokens.colors.interactive.primary__selected_highlight.hex,
        navItem: {
          hoverBackground: tokens.colors.interactive.primary__hover.hex,
        },
        submenu: {
          background: VariaTheme.navigation.submenu.background,
        },
      },
      text: {
        primary: VariaTheme.text.primary,
        secondary: VariaTheme.text.secondary,
      },
      custom: {
        sidebarGroupLabel: 'rgba(255, 255, 255, 0.6)', // varia specific color
        sidebarGroupLabelDivider: 'rgba(255, 255, 255, 0.12)', // varia specific color
      },
    },
    typography: Typography,
  }),
  pageTheme: pageThemes,
  components: {
    // make the title and subtitle a bold font
    BackstageHeader: {
      styleOverrides: {
        title: () => ({
          fontWeight: 900,
        }),
        subtitle: () => ({
          fontWeight: 900,
        }),
      },
    },
    // Backstage core uses an h5 for card titles. Ideally this should be an h3 or h4
    BackstageInfoCard: {
      styleOverrides: {
        headerTitle: () => ({
          fontSize: tokens.typography.heading.h3.fontSize,
        }),
      },
    },
    // Style chips to match the Varia light theme instead of the default MUI gray
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: VariaTheme.chipBackground, // --bui-bg-surface-2
          color: VariaTheme.text.primary,
          '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus': {
            backgroundColor: VariaTheme.chipHoverBackground, // --varia-background-color-button-hover-lightmode
            color: VariaTheme.chipTextColor,
          },
          '&.MuiChip-clickable:active': {
            backgroundColor: VariaTheme.chipPressedBackground, // --varia-background-color-button-pressed-lightmode
            color: VariaTheme.chipTextColor,
          },
        },
      },
    },
    // Bring the dismissable banner component in line with Varia styling
    BackstageDismissableBanner: {
      styleOverrides: {
        info: () => ({
          backgroundColor: VariaTheme.infoBackground,
          color: '#1d1d1d',
          '& button': {
            color: '#1d1d1d',
          },
        }),
        error: () => ({
          backgroundColor: VariaTheme.errorBackground,
          color: '#ffffff',
          '& button': {
            color: '#ffffff',
          },
        }),
        message: () => ({
          backgroundColor: 'inherit',
        }),
      },
    },
  },
  defaultPageTheme: 'home',
});
